import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CancelIcon from "@mui/icons-material/Cancel";

import { getDecryptedAccessToken } from "../../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../../../Utils/ApiServices/ApiServices";

const statusOptions = [
  { value: "", label: "All Orders" },
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "preparing", label: "Preparing" },
  { value: "ready", label: "Ready" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const statusColor = {
  pending: "warning",
  accepted: "info",
  preparing: "primary",
  ready: "secondary",
  completed: "success",
  cancelled: "error",
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "$0.00";
  const num = Number(value);
  if (Number.isNaN(num)) return `$${value}`;
  return `$${num.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const ClientOrders = () => {
  const clientId = sessionStorage.getItem("activeClientId");
  const token = getDecryptedAccessToken();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [prepTime, setPrepTime] = useState(20);

  const [actionLoading, setActionLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getArrayData = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.results)) return response.results;
    if (Array.isArray(response?.data?.results)) return response.data.results;
    return [];
  };

  const getObjectData = (response) => {
    if (response?.data && typeof response.data === "object") return response.data;
    if (response && typeof response === "object") return response;
    return null;
  };

  const fetchOrders = async () => {
    if (!clientId) {
      showSnackbar("Client ID not found.", "error");
      return;
    }

    setLoading(true);

    try {
      const queryParams = new URLSearchParams();

      if (statusFilter) queryParams.append("status", statusFilter);
      if (dateFilter) queryParams.append("date", dateFilter);
      if (locationFilter.trim()) {
        queryParams.append("location_id", locationFilter.trim());
      }

      const queryString = queryParams.toString();

      const response = await ApiServices.get(
        `/api/v1/customers/restaurant/${clientId}/orders/${
          queryString ? `?${queryString}` : ""
        }`,
        { Authorization: `Bearer ${token}` }
      );

      const data = getArrayData(response);

      const formattedOrders = data.map((order, index) => ({
        id: order.id || index + 1,
        order_id: order.id,
        order_number: order.order_number || "-",
        service_type: order.service_type || "-",
        location_id: order.location_id || "-",
        status: order.status || "-",
        payment_method: order.payment_method || "-",
        subtotal: order.subtotal || "0.00",
        discount: order.discount || "0.00",
        delivery_fee: order.delivery_fee || "0.00",
        total: order.total || "0.00",
        placed_at: order.placed_at || "-",
        updated_at: order.updated_at || "-",
        special_instructions: order.special_instructions || "-",
        remaining_prep_seconds: order.remaining_prep_seconds,
        raw: order,
      }));

      formattedOrders.sort((a, b) => {
        const dateA = new Date(a.placed_at).getTime() || 0;
        const dateB = new Date(b.placed_at).getTime() || 0;
        return dateB - dateA;
      });

      setOrders(formattedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
      showSnackbar(
        error?.response?.data?.detail || "Failed to fetch orders.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, statusFilter, dateFilter]);

  const openOrderDetail = async (order) => {
    setActionLoading(true);

    try {
      const response = await ApiServices.get(
        `/api/v1/customers/restaurant/${clientId}/orders/${order.order_id}/`,
        { Authorization: `Bearer ${token}` }
      );

      const detail = getObjectData(response);
      setSelectedOrder(detail || order.raw);
      setDetailOpen(true);
    } catch (error) {
      console.error("Error fetching order detail:", error);
      setSelectedOrder(order.raw);
      setDetailOpen(true);
      showSnackbar("Showing available order details.", "warning");
    } finally {
      setActionLoading(false);
    }
  };

  const openAcceptDialog = (order) => {
    setSelectedOrder(order.raw);
    setPrepTime(20);
    setAcceptOpen(true);
  };

  const acceptOrder = async () => {
    if (!prepTime || Number(prepTime) < 1 || Number(prepTime) > 480) {
      showSnackbar("Prep time must be between 1 and 480 minutes.", "error");
      return;
    }

    setActionLoading(true);

    try {
      await ApiServices.post(
        `/api/v1/customers/restaurant/${clientId}/orders/${selectedOrder.id}/accept/`,
        { prep_time_minutes: Number(prepTime) },
        { Authorization: `Bearer ${token}` }
      );

      showSnackbar("Order accepted successfully.", "success");
      setAcceptOpen(false);
      fetchOrders();
    } catch (error) {
      console.error("Accept order error:", error);
      showSnackbar(
        error?.response?.data?.detail || "Failed to accept order.",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const updateOrderStatus = async (order, status, note = "") => {
    setActionLoading(true);

    try {
      await ApiServices.post(
        `/api/v1/customers/restaurant/${clientId}/orders/${order.order_id}/status/`,
        { status, note },
        { Authorization: `Bearer ${token}` }
      );

      showSnackbar(`Order marked as ${status}.`, "success");
      fetchOrders();
    } catch (error) {
      console.error("Update order status error:", error);
      showSnackbar(
        error?.response?.data?.detail || "Failed to update order status.",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setDateFilter("");
    setLocationFilter("");
  };

  const getNextActions = (order) => {
    if (order.status === "pending") {
      return (
        <Tooltip title="Accept order">
          <IconButton
            color="success"
            onClick={() => openAcceptDialog(order)}
            disabled={actionLoading}
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      );
    }

    if (order.status === "accepted") {
      return (
        <>
          <Tooltip title="Start preparing">
            <IconButton
              color="primary"
              onClick={() =>
                updateOrderStatus(order, "preparing", "Started cooking")
              }
              disabled={actionLoading}
            >
              <RestaurantIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Cancel order">
            <IconButton
              color="error"
              onClick={() => updateOrderStatus(order, "cancelled", "Cancelled")}
              disabled={actionLoading}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </>
      );
    }

    if (order.status === "preparing") {
      return (
        <>
          <Tooltip title="Mark ready">
            <IconButton
              color="secondary"
              onClick={() => updateOrderStatus(order, "ready", "Order ready")}
              disabled={actionLoading}
            >
              <DoneAllIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Cancel order">
            <IconButton
              color="error"
              onClick={() => updateOrderStatus(order, "cancelled", "Cancelled")}
              disabled={actionLoading}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </>
      );
    }

    if (order.status === "ready") {
      return (
        <Tooltip title="Mark completed">
          <IconButton
            color="success"
            onClick={() =>
              updateOrderStatus(order, "completed", "Order completed")
            }
            disabled={actionLoading}
          >
            <DoneAllIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 190,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="View order details">
            <IconButton
              color="info"
              onClick={() => openOrderDetail(params.row)}
              disabled={actionLoading}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>

          {getNextActions(params.row)}
        </Stack>
      ),
    },
    {
      field: "order_number",
      headerName: "Order ID",
      minWidth: 210,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => openOrderDetail(params.row)}
          sx={{
            p: 0,
            minWidth: "auto",
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: "placed_at",
      headerName: "Order Date & Time",
      minWidth: 210,
      flex: 1,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      field: "service_type",
      headerName: "Service",
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "payment_method",
      headerName: "Payment",
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ textTransform: "uppercase" }}
        />
      ),
    },
    {
      field: "total",
      headerName: "Total",
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Typography fontWeight={700}>{formatMoney(params.value)}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={params.value || "-"}
          color={statusColor[params.value] || "default"}
          size="small"
          sx={{ textTransform: "capitalize", fontWeight: 600 }}
        />
      ),
    },
    {
      field: "special_instructions",
      headerName: "Instructions",
      minWidth: 260,
      flex: 1.2,
    },
  ];

  const selectedItems = useMemo(() => {
    if (!selectedOrder) return [];
    return selectedOrder.items || [];
  }, [selectedOrder]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Restaurant Orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest orders are shown at the top.
              </Typography>
            </Box>

            <Button variant="contained" onClick={fetchOrders}>
              Refresh
            </Button>
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <TextField
              select
              size="small"
              label="Order Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Order Date"
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 220 }}
            />

            <TextField
              size="small"
              label="Location ID"
              placeholder="Optional branch/location id"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              sx={{ minWidth: 280 }}
            />

            <Button variant="outlined" onClick={fetchOrders}>
              Apply
            </Button>

            <Button color="inherit" onClick={clearFilters}>
              Clear
            </Button>
          </Stack>

          <Box sx={{ width: "100%", height: 620 }}>
            <DataGrid
              rows={orders}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
              }}
              slots={{
                loadingOverlay: CircularProgress,
              }}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f9fafb",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Accept Order</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="Estimated Prep Time"
            type="number"
            value={prepTime}
            onChange={(e) => setPrepTime(e.target.value)}
            margin="normal"
            helperText="Enter prep time between 1 and 480 minutes"
            inputProps={{ min: 1, max: 480 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAcceptOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={acceptOrder}
            disabled={actionLoading}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Order Details -{" "}
          {selectedOrder?.order_number || selectedOrder?.id || "-"}
        </DialogTitle>

        <DialogContent dividers>
          {selectedOrder ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography>
                    <strong>Order Number:</strong>{" "}
                    {selectedOrder.order_number || "-"}
                  </Typography>
                  <Typography>
                    <strong>Order Date & Time:</strong>{" "}
                    {formatDateTime(selectedOrder.placed_at)}
                  </Typography>
                  <Typography>
                    <strong>Status:</strong>{" "}
                    <Chip
                      label={selectedOrder.status || "-"}
                      color={statusColor[selectedOrder.status] || "default"}
                      size="small"
                      sx={{ textTransform: "capitalize", ml: 1 }}
                    />
                  </Typography>
                  <Typography>
                    <strong>Service Type:</strong>{" "}
                    {selectedOrder.service_type || "-"}
                  </Typography>
                  <Typography>
                    <strong>Payment Method:</strong>{" "}
                    {(selectedOrder.payment_method || "-").toUpperCase()}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <strong>Subtotal:</strong>{" "}
                    {formatMoney(selectedOrder.subtotal)}
                  </Typography>
                  <Typography>
                    <strong>Discount:</strong>{" "}
                    {formatMoney(selectedOrder.discount)}
                  </Typography>
                  <Typography>
                    <strong>Delivery Fee:</strong>{" "}
                    {formatMoney(selectedOrder.delivery_fee)}
                  </Typography>
                  <Typography fontWeight={800}>
                    <strong>Total:</strong> {formatMoney(selectedOrder.total)}
                  </Typography>
                  <Typography>
                    <strong>Coupon:</strong>{" "}
                    {selectedOrder.coupon_code
                      ? `${selectedOrder.coupon_code} ${
                          selectedOrder.coupon_name
                            ? `(${selectedOrder.coupon_name})`
                            : ""
                        }`
                      : "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography>
                    <strong>Special Instructions:</strong>{" "}
                    {selectedOrder.special_instructions || "-"}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
                Ordered Items
              </Typography>

              {selectedItems.length > 0 ? (
                <Box sx={{ width: "100%", height: 330 }}>
                  <DataGrid
                    rows={selectedItems.map((item, index) => ({
                      id: item.id || index + 1,
                      item_name: item.item_name || "-",
                      variant_name: item.variant_name || "-",
                      quantity: item.quantity || 1,
                      unit_price: item.unit_price || "0.00",
                      line_total: item.line_total || "0.00",
                      notes: item.notes || "-",
                    }))}
                    columns={[
                      {
                        field: "item_name",
                        headerName: "Item Name",
                        flex: 1,
                        minWidth: 240,
                      },
                      {
                        field: "variant_name",
                        headerName: "Variant",
                        flex: 0.8,
                        minWidth: 150,
                      },
                      {
                        field: "quantity",
                        headerName: "Qty",
                        flex: 0.4,
                        minWidth: 80,
                      },
                      {
                        field: "unit_price",
                        headerName: "Unit Price",
                        flex: 0.7,
                        minWidth: 130,
                        renderCell: (params) => formatMoney(params.value),
                      },
                      {
                        field: "line_total",
                        headerName: "Line Total",
                        flex: 0.7,
                        minWidth: 130,
                        renderCell: (params) => (
                          <Typography fontWeight={700}>
                            {formatMoney(params.value)}
                          </Typography>
                        ),
                      },
                      {
                        field: "notes",
                        headerName: "Notes",
                        flex: 1,
                        minWidth: 180,
                      },
                    ]}
                    hideFooter
                    disableRowSelectionOnClick
                  />
                </Box>
              ) : (
                <Alert severity="info">No item details found.</Alert>
              )}

              {selectedOrder.status_logs?.length > 0 && (
                <>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mt: 3, mb: 1 }}
                  >
                    Status Logs
                  </Typography>

                  <Stack spacing={1}>
                    {selectedOrder.status_logs.map((log, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          border: "1px solid #e5e7eb",
                          borderRadius: 2,
                        }}
                      >
                        <Typography fontWeight={700}>
                          {log.status || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(log.changed_at)}
                        </Typography>
                        {log.note && (
                          <Typography variant="body2">{log.note}</Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientOrders;